/**
 * 会員情報の変更（#member-edit-form）のクライアント側バリデーション
 * visit__fieldError / is-invalid（会員登録・資料請求と同系）
 */
import { isValidPhoneTel, PHONE_TEL_FORMAT_MESSAGE } from './phone-validation.js';
import { isValidEmail } from './email-validation.js';
const ZIP_RE = /^\d{3}-?\d{4}$/;
const MEMBER_PASSWORD_RE = /^[0-9A-Za-z]{1,8}$/;

function trimValue(id) {
	const el = document.getElementById(id);
	return el ? el.value.trim() : '';
}

function clearErrorForControl(form, controlId) {
	form.querySelectorAll(`.visit__fieldError[data-for="${controlId}"]`).forEach((n) => n.remove());
	const control = document.getElementById(controlId);
	if (!control) return;
	control.removeAttribute('aria-invalid');
	control.classList.remove('is-invalid');
	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) wrap.classList.remove('is-invalid');
	const schedule = control.closest('.visit__schedule');
	if (schedule) schedule.classList.remove('is-invalid');
	const consent = control.closest('.visit__consent');
	if (consent) consent.classList.remove('is-invalid');
	if (control instanceof HTMLFieldSetElement) {
		return;
	}
}

export function clearMemberEditFormErrors(form) {
	form.querySelectorAll('.visit__fieldError').forEach((n) => n.remove());
	form.querySelectorAll('.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__inputWrap--date.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__schedule.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__consent.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('[aria-invalid="true"]').forEach((n) => n.removeAttribute('aria-invalid'));
}

function mountErrorParagraph(controlId, message) {
	const p = document.createElement('p');
	p.className = 'visit__fieldError';
	p.setAttribute('data-for', controlId);
	p.setAttribute('role', 'alert');
	p.textContent = message;
	return p;
}

function showFieldError(form, controlId, message) {
	const control = document.getElementById(controlId);
	if (!control) return;

	clearErrorForControl(form, controlId);

	control.setAttribute('aria-invalid', 'true');
	control.classList.add('is-invalid');

	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) {
		wrap.classList.add('is-invalid');
		wrap.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	const schedule = control.closest('.visit__schedule');
	if (schedule) {
		schedule.classList.add('is-invalid');
		schedule.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	if (control.id === 'member-edit-name-family' || control.id === 'member-edit-name-given') {
		const grid = control.closest('.visit__fieldGrid--name');
		if (grid) {
			grid.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
			return;
		}
	}

	control.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
}

function focusFirst(controlId) {
	const el = document.getElementById(controlId);
	if (!el) return;
	el.focus();
}

/**
 * @returns {boolean}
 */
export function validateMemberEditForm(form) {
	clearMemberEditFormErrors(form);

	const errors = [];

	const family = trimValue('member-edit-name-family');
	const given = trimValue('member-edit-name-given');
	if (!family && !given) {
		errors.push(['member-edit-name-family', '性・名を入力してください。']);
	} else if (!family) {
		errors.push(['member-edit-name-family', '性を入力してください。']);
	} else if (!given) {
		errors.push(['member-edit-name-given', '名を入力してください。']);
	}

	const email = trimValue('member-edit-email');
	if (!email) {
		errors.push(['member-edit-email', 'メールアドレスを入力してください。']);
	} else if (!isValidEmail(email)) {
		errors.push(['member-edit-email', 'メールアドレスの形式が正しくありません。']);
	}

	const pwd = trimValue('member-edit-password');
	const pwd2 = trimValue('member-edit-password-confirm');
	if (!pwd) {
		errors.push(['member-edit-password', '新しいパスワードを入力してください。']);
	} else if (!MEMBER_PASSWORD_RE.test(pwd)) {
		errors.push(['member-edit-password', 'パスワードは半角英数字1〜8文字で入力してください。']);
	}

	if (!errors.some(([id]) => id === 'member-edit-password')) {
		if (!pwd2) {
			errors.push(['member-edit-password-confirm', '確認のためパスワードを入力してください。']);
		} else if (pwd !== pwd2) {
			errors.push(['member-edit-password-confirm', 'パスワードが一致しません。']);
		}
	}

	/* 電話は任意：入力があるときだけ半角数字10〜11桁を検証する */
	const telRaw = trimValue('member-edit-tel');
	if (telRaw && !isValidPhoneTel(telRaw)) {
		errors.push(['member-edit-tel', PHONE_TEL_FORMAT_MESSAGE]);
	}

	/* ご住所は任意：郵便番号に入力があるときだけ形式を検証する */
	const zip = trimValue('member-edit-zip');
	if (zip) {
		const zipClean = zip.replace(/[〒\s]/g, '');
		if (!ZIP_RE.test(zipClean)) {
			errors.push(['member-edit-zip', '郵便番号は7桁の半角数字で入力してください（ハイフン可）。']);
		}
	}

	if (errors.length === 0) {
		return true;
	}

	for (const [id, msg] of errors) {
		showFieldError(form, id, msg);
	}
	focusFirst(errors[0][0]);
	return false;
}

export function initMemberEditFormValidation(form) {
	if (!form || form.id !== 'member-edit-form') return;

	const onEdit = (e) => {
		const t = e.target;
		if (!t || !form.contains(t)) return;
		if (t.id) {
			clearErrorForControl(form, t.id);
		}
		if (t.id === 'member-edit-password' || t.id === 'member-edit-password-confirm') {
			clearErrorForControl(form, 'member-edit-password');
			clearErrorForControl(form, 'member-edit-password-confirm');
		}
	};

	form.addEventListener('input', onEdit);
	form.addEventListener('change', onEdit);
}
